import { motion } from 'framer-motion';
import { Check } from 'lucide-react';
import React from 'react';
import { useI18n } from '../../i18n/I18nProvider';

const Features: React.FC = () => {
  const { t } = useI18n();

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  return (
    <section id="features" className="py-16 md:py-24 relative overflow-hidden">
      <motion.div
        className="absolute inset-0 bg-gradient-to-br from-gray-50 via-blue-50 to-purple-50"
        animate={{
          background: [
            'linear-gradient(to bottom right, #f9fafb, #f0f9ff, #faf5ff)',
            'linear-gradient(to bottom right, #f9fafb, #e0f2fe, #f5f3ff)',
            'linear-gradient(to bottom right, #f9fafb, #dbeafe, #ede9fe)',
            'linear-gradient(to bottom right, #f9fafb, #f0f9ff, #faf5ff)',
          ],
        }}
        transition={{
          duration: 10,
          repeat: Infinity,
          repeatType: "reverse",
        }}
      />
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <motion.div 
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <motion.h2 
            className="text-3xl md:text-4xl font-bold font-heading mb-4 bg-clip-text text-transparent bg-gradient-to-r from-blue-600 to-purple-600"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.2 }}
          >
            {t.features.title}
          </motion.h2>
          <motion.p 
            className="text-gray-600 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.4 }}
          >
            {t.features.description}
          </motion.p>
        </motion.div>

        <motion.div 
          className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
          variants={containerVariants}
          initial="hidden"
          whileInView="visible"
          viewport={{ once: true }}
        >
          {t.features.items.map((feature, index) => (
            <motion.div 
              key={index} 
              className="bg-white rounded-lg p-6 shadow-md hover:shadow-lg transition-shadow duration-300 border border-gray-100"
              variants={itemVariants}
              whileHover={{ 
                scale: 1.02,
                transition: { duration: 0.2 }
              }}
            >
              <div className="flex items-center mb-4">
                <motion.div 
                  className="w-8 h-8 mr-3 rounded-full bg-testingLib-blue/10 flex items-center justify-center"
                  whileHover={{ 
                    scale: 1.1,
                    backgroundColor: "rgba(59, 130, 246, 0.2)"
                  }}
                >
                  <Check className="h-4 w-4 text-testingLib-blue" />
                </motion.div>
                <h3 className="text-xl font-bold text-primary">{feature.title}</h3>
              </div>
              <p className="text-gray-600">{feature.description}</p>
            </motion.div>
          ))}
        </motion.div>
        
        <motion.div 
          className="mt-16 bg-white rounded-xl shadow-lg p-8 max-w-4xl mx-auto border border-gray-100"
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          whileHover={{ 
            scale: 1.01,
            transition: { duration: 0.2 }
          }}
        >
          <div className="flex flex-col md:flex-row gap-6 items-center">
            <motion.div 
              className="flex-shrink-0 w-24 h-24 bg-gradient-to-br from-primary to-accent rounded-lg flex items-center justify-center shadow-lg"
              animate={{
                background: [
                  'linear-gradient(to bottom right, var(--primary), var(--accent))',
                  'linear-gradient(to bottom right, var(--accent), var(--primary))',
                  'linear-gradient(to bottom right, var(--primary), var(--accent))',
                ],
              }}
              transition={{
                duration: 4,
                repeat: Infinity,
                repeatType: "reverse",
              }}
            >
              <motion.svg 
                xmlns="http://www.w3.org/2000/svg" 
                width="48" 
                height="48" 
                viewBox="0 0 48 48" 
                fill="none" 
                className="drop-shadow-lg"
                animate={{
                  rotate: [0, 360],
                }}
                transition={{
                  duration: 20,
                  repeat: Infinity,
                  ease: "linear"
                }}
              >
                <motion.path 
                  d="M24 6L6 18V30L24 42L42 30V18L24 6Z" 
                  stroke="#3b82f6" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  animate={{
                    stroke: ["#3b82f6", "#60a5fa", "#3b82f6"],
                    filter: ["drop-shadow(0 0 2px #3b82f6)", "drop-shadow(0 0 4px #60a5fa)", "drop-shadow(0 0 2px #3b82f6)"],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                  }}
                />
                <motion.path 
                  d="M24 6L24 42" 
                  stroke="#3b82f6" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  animate={{
                    stroke: ["#3b82f6", "#60a5fa", "#3b82f6"],
                    filter: ["drop-shadow(0 0 2px #3b82f6)", "drop-shadow(0 0 4px #60a5fa)", "drop-shadow(0 0 2px #3b82f6)"],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 0.5
                  }}
                />
                <motion.path 
                  d="M6 18L42 30" 
                  stroke="#3b82f6" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  animate={{
                    stroke: ["#3b82f6", "#60a5fa", "#3b82f6"],
                    filter: ["drop-shadow(0 0 2px #3b82f6)", "drop-shadow(0 0 4px #60a5fa)", "drop-shadow(0 0 2px #3b82f6)"],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 1
                  }}
                />
                <motion.path 
                  d="M42 18L6 30" 
                  stroke="#3b82f6" 
                  strokeWidth="3" 
                  strokeLinecap="round" 
                  strokeLinejoin="round"
                  animate={{
                    stroke: ["#3b82f6", "#60a5fa", "#3b82f6"],
                    filter: ["drop-shadow(0 0 2px #3b82f6)", "drop-shadow(0 0 4px #60a5fa)", "drop-shadow(0 0 2px #3b82f6)"],
                  }}
                  transition={{
                    duration: 2,
                    repeat: Infinity,
                    delay: 1.5
                  }}
                />
              </motion.svg>
            </motion.div>
            <div>
              <h3 className="text-2xl font-bold font-heading mb-2">TDD Approach</h3>
              <p className="text-gray-600">
                El e-book incluye una guía completa sobre Test Driven Development en Angular, una metodología que puede transformar completamente tu proceso de desarrollo y la calidad de tu código.
              </p>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default Features;
